import { ref, Ref } from 'vue';
import { useNotify, NotifyType } from '@/composables/useNotify';
import { useI18n } from 'vue-i18n';
import { hubClient, getApiErrorMessage } from '@tfpl/api-client';
import { getConfig } from '@/config';

interface UseCollabNote {
  loading: Ref<boolean>;
  error: Ref<Error>;
  showError: Ref<boolean>;
  deleteNote: (uuid: string) => Promise<void>;
  createNote: (dto: hubClient.CreateCollaboratorNoteDto) => Promise<void>;
  updateNote: (uuid: string, dto: hubClient.UpdateCollaboratorNoteDto) => Promise<void>;
}

export const useCollabNote: () => UseCollabNote = () => {
  const { notify } = useNotify();
  const { t } = useI18n();
  const loading = ref(false);
  const error = ref();
  const showError = ref(false);
  const setError = (err: boolean): void => {
    showError.value = err;
  };

  const createNote = async (dto: hubClient.CreateCollaboratorNoteDto) => {
    try {
      loading.value = true;
      await hubClient.V3CollaboratorNotesService.createOneV3({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
        },
        body: dto,
        throwOnError: true,
      });
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('common.requestError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const updateNote = async (uuid: string, dto: hubClient.UpdateCollaboratorNoteDto) => {
    try {
      loading.value = true;
      await hubClient.V3CollaboratorNotesService.updateOneV3({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
          uuid,
        },
        body: dto,
        throwOnError: true,
      });
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('common.requestError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const deleteNote = async (uuid: string) => {
    try {
      loading.value = true;
      await hubClient.V3CollaboratorNotesService.deleteOneV3({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
          uuid,
        },
        throwOnError: true,
      });
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('common.requestError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  return { loading, error, showError, createNote, updateNote, deleteNote };
};
